import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { BookOpen, Clock, Layers, Play, Search, Building2 } from 'lucide-react'
import { useCourses } from '@/hooks/useCourses'
import { Input } from '@/components/ui/input'
import { paths } from '@/routes/path'
import { Select } from '@/components/ui/Select'

type FeedFilters = {
  search?: string
  status?: string
  department?: string
}

export function CourseFeedPage() {
  const [filters, setFilters] = useState<FeedFilters>({})

  const { data: response, isLoading, error } = useCourses({ search: filters.search })
  const courses = response?.data ?? []

  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const matchesStatus = !filters.status || course.activityStatus === filters.status
      const matchesDepartment =
        !filters.department ||
        course.departmentName === filters.department ||
        course.programmeTitle === filters.department

      return matchesStatus && matchesDepartment
    })
  }, [courses, filters.status, filters.department])

  const departmentOptions = useMemo(
    () =>
      Array.from(
        new Set(
          courses
            .map((course) => course.departmentName || course.programmeTitle || null)
            .filter((value): value is string => Boolean(value)),
        ),
      ),
    [courses],
  )

  const statusCounts = useMemo(
    () =>
      courses.reduce<Record<string, number>>((acc, course) => {
        const status = course.activityStatus
        acc[status] = (acc[status] ?? 0) + 1
        return acc
      }, {}),
    [courses],
  )

  const statusBadgeMap: Record<string, string> = {
    active: 'bg-green-100 text-green-700 border-green-200',
    upcoming: 'bg-blue-100 text-blue-700 border-blue-200',
    completed: 'bg-purple-100 text-purple-700 border-purple-200',
    inactive: 'bg-gray-100 text-gray-700 border-gray-200',
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        <BookOpen className="mb-4 h-12 w-12 text-primary-400" />
        Failed to load courses. Please try again later.
      </div>
    )
  }

  return (
    <div className="space-y-6 bg-gradient-to-br from-purple-50 to-primary-50 px-4 py-6 sm:px-6 lg:px-10">
      <header className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-secondary-900">Course Library</h1>
          <p className="text-secondary-500">
            Explore available programmes and continue where you left off.
          </p>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="w-full max-w-md">
            <Input
              placeholder="Search courses by title or code"
              value={filters.search ?? ''}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              icon={<Search className="h-4 w-4 text-primary-500" />}
            />
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <div className="w-full sm:w-44">
              <Select
                value={filters.status ?? ''}
                onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value || undefined }))}
              >
                <option value="">All status ({courses.length})</option>
                <option value="active">Active ({statusCounts.active ?? 0})</option>
                <option value="upcoming">Upcoming ({statusCounts.upcoming ?? 0})</option>
                <option value="completed">Completed ({statusCounts.completed ?? 0})</option>
                <option value="inactive">Inactive ({statusCounts.inactive ?? 0})</option>
              </Select>
            </div>
            <div className="w-full sm:w-56">
              <Select
                value={filters.department ?? ''}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    department: event.target.value || undefined,
                  }))
                }
              >
                <option value="">All departments ({courses.length})</option>
                {departmentOptions.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </header>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="h-56 animate-pulse bg-white/70 shadow-md" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filteredCourses.map((course) => (
              <Card key={course.id} className="overflow-hidden border-0 bg-white shadow-md transition hover:shadow-lg">
                <CardHeader className="space-y-3 bg-gradient-to-br from-primary-100 via-white to-white">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className={statusBadgeMap[course.activityStatus] ?? statusBadgeMap.active}>
                      {course.activityStatus.toUpperCase()}
                    </Badge>
                    <span className="text-xs uppercase tracking-wide text-secondary-400">
                      {course.code ?? 'No code'}
                    </span>
                  </div>
                  <CardTitle className="text-xl text-secondary-900">{course.title}</CardTitle>
                  <CardDescription className="text-sm text-secondary-500">
                    {course.description ?? 'No additional description provided yet.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 py-5">
                  <div className="flex items-center gap-3 text-sm text-secondary-600">
                    <Building2 className="h-4 w-4 text-primary-500" />
                    <span>{course.departmentName ?? course.programmeTitle ?? 'Unassigned department'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-secondary-600">
                    <Clock className="h-4 w-4 text-primary-500" />
                    <span>{course.durationLabel ?? 'Flexible schedule'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-secondary-600">
                    <Layers className="h-4 w-4 text-primary-500" />
                    <span>{course.modules.length} module{course.modules.length === 1 ? '' : 's'}</span>
                  </div>
                  <Link to={paths.app.courses.detail.build(course.id)}>
                    <Button className="w-full bg-primary-600 hover:bg-primary-700 text-white">
                      <Play className="mr-2 h-4 w-4" />
                      View Course
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          {filteredCourses.length === 0 && !isLoading && (
            <Card className="border border-dashed border-primary-200 bg-white/70 py-16 text-center shadow-none">
              <CardContent>
                <BookOpen className="mx-auto h-12 w-12 text-primary-400" />
                <p className="mt-4 text-lg font-semibold text-secondary-700">No courses match your filters</p>
                <p className="mt-2 text-sm text-secondary-500">
                  Try clearing the filters or search for another keyword.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
